"use client";

import React, { useEffect, useState, useCallback, useMemo, memo, FC } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "./command";
import { ChevronsUpDown } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";
import { useTranslations } from "use-intl";
export type Option = {
  value: string | boolean | number;
  label: string;
  subtitle?: string;
  logo?: string | null;
};
export type AutocompleteProps = {
  options: Option[];
  placeholder?: string;
  onChangeAction?: (newValue: Option) => void;
  initialValue?: string | boolean | number | null;
  onQueryChange?: (query: string) => void;
  extraStyles?: string;
  trimLength?: number;
  disabled?: boolean;
  width?: number;
  fullWidth?: boolean;
  logoImageSelectorAction?: (option: Option) => string | null | undefined;
  largeItems?: boolean;
  disableFiltering?: boolean;
};
const DEFAULT_TRIM = 20;
const trimText = (text: string, maxLength: number) => text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
const normalizeString = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
type OptionItemProps = {
  option: Option;
  isSelected: boolean;
  onSelect: (option: Option, isSelected: boolean) => void;
  logo: string | null | undefined;
  largeItems: boolean;
};
const OptionItem: FC<OptionItemProps> = memo(({
  option,
  isSelected,
  onSelect,
  logo,
  largeItems
}) => <CommandItem key={String(option.value)} onSelect={() => onSelect(option, isSelected)} value={option.label} className="group relative flex cursor-pointer items-start gap-2 overflow-hidden rounded-2xl !bg-[#dbeafe30] px-4 py-5 transition-all duration-200 ease-in-out">
      <div className={cn("absolute inset-0 rounded-lg transition-opacity duration-200 ease-in-out", isSelected ? "bg-[linear-gradient(to_right,#dbeafe30_0%,#dbeafe80_40%,#0e72dc60_100%)] opacity-100" : "bg-[linear-gradient(to_right,#dbeafe30_0%,#dbeafe80_60%,#0e72dc60_100%)] opacity-0 group-hover:opacity-100")} />
      <div className="relative flex items-center gap-2">
        {logo && <img src={logo} alt={option.label} className={cn("shrink-0", largeItems ? "h-6 w-6" : "h-4 w-4")} />}
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold">{option.label}</span>
          {option.subtitle && <span className="text-xs text-gray-500">{option.subtitle}</span>}
        </div>
      </div>
    </CommandItem>);
const Autocomplete: FC<AutocompleteProps> = ({
  options,
  placeholder = "Select an option",
  onChangeAction = () => {},
  initialValue = "",
  onQueryChange,
  extraStyles = "",
  disabled = false,
  trimLength = DEFAULT_TRIM,
  width = 300,
  fullWidth = false,
  logoImageSelectorAction = opt => opt.logo,
  largeItems = false,
  disableFiltering = false
}) => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | boolean | number | null>(initialValue);
  const [query, setQuery] = useState("");

  // Sync external value changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);
  const selectedOption = useMemo(() => options.find(opt => opt.value === value), [options, value]);
  const handleSelect = useCallback((option: Option, isSelected: boolean) => {
    const newValue = isSelected ? "" : option.value;
    setValue(newValue);
    setOpen(false);
    onChangeAction(isSelected ? {
      value: "",
      label: ""
    } : option);
  }, [onChangeAction]);
  const handleInputChange = useCallback((val: string) => {
    setQuery(val);
    onQueryChange?.(val);
  }, [onQueryChange]);
  const filteredOptions = useMemo(() => {
    if (disableFiltering) return options;
    const q = query.trim();
    if (!q) return options;
    const normQ = normalizeString(q);
    const exactMatch = options.find(opt => normalizeString(opt.label) === normQ);
    if (exactMatch) return [exactMatch];
    return options.filter(opt => normalizeString(opt.label).includes(normQ));
  }, [options, query, disableFiltering]);
  return <Popover open={open} onOpenChange={setOpen} data-sentry-element="Popover" data-sentry-component="Autocomplete" data-sentry-source-file="autocomplete.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="autocomplete.tsx">
        <Button disabled={disabled} variant="outline" role="combobox" aria-expanded={open} className={cn("h-12 w-full justify-between rounded-3xl", extraStyles)} data-sentry-element="Button" data-sentry-source-file="autocomplete.tsx">
          {value ? trimText(selectedOption?.label || "", trimLength) : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" data-sentry-element="ChevronsUpDown" data-sentry-source-file="autocomplete.tsx" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="rounded-3xl border p-2 shadow-md" style={{
      maxWidth: fullWidth ? "100%" : `min(${width}px, 100vw)`,
      minWidth: width ? `min(${width}px, 100vw)` : undefined
    }} data-sentry-element="PopoverContent" data-sentry-source-file="autocomplete.tsx">
        <Command shouldFilter={false} data-sentry-element="Command" data-sentry-source-file="autocomplete.tsx">
          <CommandInput placeholder={t("placeholder_search_20230807140502")} onValueChange={handleInputChange} data-sentry-element="CommandInput" data-sentry-source-file="autocomplete.tsx" />
          <CommandList data-sentry-element="CommandList" data-sentry-source-file="autocomplete.tsx">
            <CommandGroup className="py-0" data-sentry-element="CommandGroup" data-sentry-source-file="autocomplete.tsx">
              {filteredOptions.map(opt => <OptionItem key={String(opt.value)} option={opt} isSelected={opt.value === value} onSelect={handleSelect} logo={logoImageSelectorAction(opt)} largeItems={largeItems} />)}
            </CommandGroup>
            {filteredOptions.length === 0 && <CommandEmpty>{t("src_components_ui_autocomplete_tsx_1")}</CommandEmpty>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>;
};
export default Autocomplete;